import { installMailerLiteIframeProxy } from '@/utils/mailerlite-iframe-proxy';

export type MailerLiteSubscribeData = {
  email: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  zipCode?: string;
};

const WEBFORM_SUCCESS_PREFIX = 'ml_webform_success';
const SUCCESS_PANEL_SELECTOR =
  '.row-success, .ml-block-success, .ml-form-successBody, .ml-form-success';
const FORM_CONTAINER_SELECTOR =
  '.ml-form-embedContainer, .ml-subscribe-form, [id^="mlb2-"], .ml-embedded[data-form]';

type WrappedFn = ((...args: unknown[]) => unknown) & { __mailerLiteSubscribeWrapped?: boolean };

const getInputValue = (container: ParentNode, selectors: string[]): string | undefined => {
  for (const selector of selectors) {
    const value = (container.querySelector(selector) as HTMLInputElement | null)?.value?.trim();
    if (value) {
      return value;
    }
  }

  return undefined;
};

const extractMailerLiteSubscribeData = (container: ParentNode): MailerLiteSubscribeData | null => {
  const email = getInputValue(container, [
    'input[name="fields.email"]',
    'input[name="fields[email]"]',
    'input[type="email"]',
    'input[name="email"]',
  ]);

  if (!email) {
    return null;
  }

  const firstName =
    getInputValue(container, [
      'input[name="fields.first_name"]',
      'input[name="fields[first_name]"]',
      'input[name="first_name"]',
      'input[autocomplete="given-name"]',
    ]) ??
    getInputValue(container, [
      'input[name="fields.name"]',
      'input[name="fields[name]"]',
      'input[autocomplete="name"]',
    ]);

  const lastName = getInputValue(container, [
    'input[name="fields.last_name"]',
    'input[name="fields[last_name]"]',
    'input[name="last_name"]',
    'input[autocomplete="family-name"]',
  ]);

  const phone = getInputValue(container, [
    'input[name="fields.phone"]',
    'input[name="fields[phone]"]',
    'input[type="tel"]',
    'input[name="phone"]',
  ]);

  const zipCode = getInputValue(container, [
    'input[name="fields.zip"]',
    'input[name="fields[zip]"]',
    'input[name="fields[zip_code]"]',
    'input[autocomplete="postal-code"]',
  ]);

  return {
    email,
    ...(phone ? { phone } : {}),
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...(zipCode ? { zipCode } : {}),
  };
};

const collectMailerLiteFormIds = (root: ParentNode = document): string[] => {
  const ids = new Set<string>();

  root.querySelectorAll('.ml-embedded[data-form]').forEach(el => {
    const formId = el.getAttribute('data-form');
    if (formId) {
      ids.add(formId);
    }
  });

  root.querySelectorAll('[id^="mlb2-"]').forEach(el => {
    ids.add(el.id.replace(/^mlb2-/, ''));
  });

  root.querySelectorAll('[class*="ml-subscribe-form-"]').forEach(el => {
    const match = el.className.match(/ml-subscribe-form-(\w+)/);
    if (match) {
      ids.add(match[1]);
    }
  });

  return [...ids];
};

const findFormContainer = (formId: string): ParentNode | null =>
  document.querySelector(`.ml-subscribe-form-${formId}`) ??
  document.querySelector(`#mlb2-${formId}`) ??
  document.querySelector(`.ml-embedded[data-form="${formId}"]`);

const getFormIdFromSuccessKey = (key: string): string | undefined => {
  if (key === WEBFORM_SUCCESS_PREFIX) {
    return undefined;
  }

  if (!key.startsWith(`${WEBFORM_SUCCESS_PREFIX}_`)) {
    return undefined;
  }

  return key.slice(WEBFORM_SUCCESS_PREFIX.length + 1);
};

const isSuccessPanelVisible = (el: Element): boolean => {
  // use the element's own document view so it works for elements inside an iframe
  const view = el.ownerDocument?.defaultView ?? window;
  const style = view.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
};

const handleSuccessFromContainer = (
  container: ParentNode,
  onSuccess: (data: MailerLiteSubscribeData) => void,
  trackedContainers: WeakSet<ParentNode>,
) => {
  if (trackedContainers.has(container)) {
    return;
  }

  const data = extractMailerLiteSubscribeData(container);
  if (!data) {
    return;
  }

  trackedContainers.add(container);
  onSuccess(data);
};

/**
 * Hooks MailerLite form success (valid submit) and forwards subscriber fields to the
 * provided callback. Supports legacy webforms (`ml_webform_success_*`), universal
 * `.ml-embedded[data-form]` containers, and popup forms (observed inside their
 * same-origin srcdoc iframe).
 */
export const setupMailerLiteSubscribeTracking = (
  onSuccess: (data: MailerLiteSubscribeData) => void,
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  installMailerLiteIframeProxy();

  const trackedContainers = new WeakSet<ParentNode>();
  const trackedIframes = new WeakSet<HTMLIFrameElement>();
  const iframeObservers: MutationObserver[] = [];
  const win = window as Window & Record<string, unknown>;

  // MailerLite popups render inside a (proxied, same-origin) srcdoc iframe, so the
  // success panel appears INSIDE the iframe where the parent observers can't see it.
  // Observe the iframe's own document and cache the email at submit time, before the
  // success view replaces the inputs.
  const attachToMailerLiteIframe = (iframe: HTMLIFrameElement) => {
    const bind = () => {
      if (trackedIframes.has(iframe)) return;

      let doc: Document | null = null;
      try {
        doc = iframe.contentDocument;
      } catch {
        return;
      }
      // wait until the srcdoc content (form or success panel) is actually present
      if (!doc?.body || !doc.querySelector(`input, form, ${SUCCESS_PANEL_SELECTOR}`)) {
        return;
      }

      trackedIframes.add(iframe);

      // The submit button is often an <a> (no native submit event), so cache the
      // field values on every input/click while they're still in the DOM.
      let lastData = extractMailerLiteSubscribeData(doc);
      const capture = () => {
        const data = extractMailerLiteSubscribeData(doc as Document);
        if (data) lastData = data;
      };
      doc.addEventListener('input', capture, true);
      doc.addEventListener('submit', capture, true);
      doc.addEventListener('click', capture, true);

      const IFRAME_SUCCESS_SELECTOR =
        '.ml-success-row, .ml-form-success, .ml-form-successBody, .row-success, .ml-block-success';
      let fired = false;
      const checkSuccess = () => {
        if (fired || !doc || !lastData || trackedContainers.has(doc)) return;
        const shown = Array.from(doc.querySelectorAll(IFRAME_SUCCESS_SELECTOR)).some(
          panel =>
            (panel as HTMLElement).getClientRects().length > 0 &&
            (panel.textContent ?? '').trim().length > 0,
        );
        if (shown) {
          fired = true;
          trackedContainers.add(doc);
          onSuccess(lastData);
        }
      };

      const innerObserver = new MutationObserver(checkSuccess);
      innerObserver.observe(doc.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
      iframeObservers.push(innerObserver);
      checkSuccess();
    };

    iframe.addEventListener('load', bind);
    bind();
  };

  const scanMailerLiteIframes = () => {
    document
      .querySelectorAll<HTMLIFrameElement>('iframe[data-mailer-lite-source-url]')
      .forEach(attachToMailerLiteIframe);
  };

  const fireForFormId = (formId: string) => {
    const container = findFormContainer(formId);
    if (container) {
      handleSuccessFromContainer(container, onSuccess, trackedContainers);
    }
  };

  const wrapSuccessHandler = (key: string) => {
    const current = win[key] as WrappedFn | undefined;
    if (typeof current === 'function' && current.__mailerLiteSubscribeWrapped) {
      return;
    }

    const original = typeof current === 'function' ? current : undefined;
    const formId = getFormIdFromSuccessKey(key);

    const wrapped: WrappedFn = function (this: unknown, ...args: unknown[]) {
      if (formId) {
        fireForFormId(formId);
      } else {
        collectMailerLiteFormIds().forEach(id => fireForFormId(id));
      }

      return original?.apply(this, args);
    };

    wrapped.__mailerLiteSubscribeWrapped = true;
    win[key] = wrapped;
  };

  const scanAndWrapHandlers = () => {
    Object.getOwnPropertyNames(window).forEach(key => {
      if (key === WEBFORM_SUCCESS_PREFIX || key.startsWith(`${WEBFORM_SUCCESS_PREFIX}_`)) {
        wrapSuccessHandler(key);
      }
    });

    collectMailerLiteFormIds().forEach(formId => {
      wrapSuccessHandler(`${WEBFORM_SUCCESS_PREFIX}_${formId}`);
    });
  };

  const scanSuccessPanels = () => {
    document.querySelectorAll(SUCCESS_PANEL_SELECTOR).forEach(panel => {
      if (!isSuccessPanelVisible(panel)) {
        return;
      }

      const container = panel.closest(FORM_CONTAINER_SELECTOR);
      if (container) {
        handleSuccessFromContainer(container, onSuccess, trackedContainers);
      }
    });
  };

  scanAndWrapHandlers();
  scanSuccessPanels();
  scanMailerLiteIframes();

  const domObserver = new MutationObserver(() => {
    scanAndWrapHandlers();
    scanSuccessPanels();
    scanMailerLiteIframes();
  });

  domObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class'],
  });

  return () => {
    domObserver.disconnect();
    iframeObservers.forEach(observer => observer.disconnect());
  };
};
